<script setup>
// Importiert notwendige Funktionen aus Vue und den eigenen Service-Dateien.
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import {
  getFileStatus,
  subscribeFileStatus,
  applyServerFileStatus,
  isFileFinished
} from '../services/localFiles'
import { resolveApiUrl } from '../services/api'
import { refreshServerNeed } from '../services/files'

// Definiert die `fileRef` als erforderliche Prop.
const props = defineProps({
  fileRef: { type: String, required: true }
})

// Lokale Zustandsvariablen für den Dateistatus, das DOM-Element und die Sichtbarkeit.
const state = ref(null)
const rootEl = ref(null)
const visible = ref(false)

// Hält Referenzen auf den Subscription-Cleaner und die Timer-IDs.
let unsubscribe = null
let observer = null
let pollTimer = null

/**
 * Formatiert eine Byte-Anzahl in Megabyte.
 * @param {number} v - Der Wert in Bytes.
 */
function formatMB(v) {
  return ((Number(v) || 0) / 1024 / 1024).toFixed(2)
}

/**
 * Bestimmt den Vorschau-Typ basierend auf dem MIME-Typ.
 * @param {string} type - Der MIME-Typ.
 */
function previewType(type) {
  if (!type) return 'file'
  if (type.startsWith('image')) return 'image'
  if (type.startsWith('video')) return 'video'
  if (type.startsWith('audio')) return 'audio'
  if (type.includes('pdf')) return 'pdf'
  return 'file'
}

// Berechnete URL für die finale, abgeschlossene Datei.
const serverFinalUrl = computed(() =>
  resolveApiUrl(`/users/files/${encodeURIComponent(props.fileRef || '')}`)
)

// Berechnete URL für den Live-Download einer noch nicht abgeschlossenen Datei.
const serverLiveUrl = computed(() =>
  resolveApiUrl(`/users/files/parts/live/${encodeURIComponent(props.fileRef || '')}`)
)

/**
 * Synchronisiert den lokalen Zustand der Komponente mit dem globalen Dateistatus-Store.
 */
function syncFromStore() {
  state.value =
    getFileStatus(props.fileRef) || {
      fileRef: props.fileRef,
      source: 'remote',
      status: 'loading',
      progress: 0,
      uploaded: 0,
      total: 0,
      type: null,
      previewUrl: serverLiveUrl.value,
      finished: false
    }
}

/**
 * Stoppt den Polling-Timer.
 */
function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

/**
 * Entscheidet, ob der Polling-Mechanismus aktiv sein sollte.
 */
function shouldPoll() {
  const s = state.value
  return Boolean(
    props.fileRef &&
      visible.value &&
      s?.source !== 'local' &&
      !isFileFinished(s)
  )
}

/**
 * Führt eine einzelne Abfrage des Server-Status durch.
 */
async function pollOnce() {
  try {
    const raw = await refreshServerNeed(props.fileRef)
    const next = applyServerFileStatus(props.fileRef, raw)
    if (next) state.value = { ...next }
  } catch {
    // Beim nächsten Tick erneut versuchen.
  } finally {
    if (isFileFinished(state.value)) stopPolling()
  }
}

/**
 * Startet den regelmäßigen Polling-Prozess, wenn er benötigt wird.
 */
function startPolling() {
  if (pollTimer || !shouldPoll()) return
  void pollOnce()
  pollTimer = window.setInterval(() => {
    if (!shouldPoll()) {
      stopPolling()
      return
    }
    void pollOnce()
  }, 1200)
}

/**
 * Richtet einen IntersectionObserver ein, um Polling nur dann zu starten,
 * wenn die Komponente im sichtbaren Bereich des Benutzers ist.
 */
function setupObserver() {
  if (!rootEl.value || !('IntersectionObserver' in window)) {
    visible.value = true
    startPolling()
    return
  }

  observer = new IntersectionObserver(
    ([entry]) => {
      visible.value = !!entry?.isIntersecting
      if (visible.value) startPolling()
      else stopPolling()
    },
    { threshold: 0.15 }
  )

  observer.observe(rootEl.value)
}

/**
 * Abonniert Änderungen am globalen Dateistatus für diese fileRef.
 */
function bindSubscription() {
  if (unsubscribe) unsubscribe()
  unsubscribe = subscribeFileStatus(props.fileRef, (s) => {
    state.value = s ? { ...s } : null
    if (isFileFinished(state.value)) stopPolling()
  })
}

/**
 * Berechnete Eigenschaft, die die korrekte URL für die Vorschau bestimmt.
 */
const displaySrc = computed(() => {
  const s = state.value

  if (s?.source === 'local' && !isFileFinished(s) && s.previewUrl) {
    return s.previewUrl
  }

  if (!isFileFinished(s) && s?.previewUrl) {
    return s.previewUrl
  }

  if (!isFileFinished(s)) {
    return serverLiveUrl.value
  }

  return serverFinalUrl.value
})

/**
 * Berechnete Eigenschaft, die die korrekte URL für den Download-Button bestimmt.
 */
const downloadHref = computed(() => {
  const s = state.value
  if (s?.source === 'local' && !isFileFinished(s) && s.previewUrl) {
    return s.previewUrl
  }
  return serverFinalUrl.value
})

/**
 * Berechnete Eigenschaft, die einen lesbaren Status-Text zurückgibt.
 */
const statusLabel = computed(() => {
  const s = state.value
  if (!s) return 'Wird geladen'
  if (s.status === 'error') return 'Fehler'
  if (isFileFinished(s)) return 'Fertig'
  if (s.source === 'local') return 'Upload läuft'
  return 'Serverstatus wird geprüft'
})

// Beobachtet die `fileRef`-Prop, um bei Änderungen alles neu zu initialisieren.
watch(
  () => props.fileRef,
  () => {
    syncFromStore()
    bindSubscription()
    stopPolling()
    if (visible.value) startPolling()
  }
)

// Lifecycle-Hooks zum Initialisieren und Aufräumen.
onMounted(() => {
  syncFromStore()
  bindSubscription()
  setupObserver()
  if (visible.value) startPolling()
})

onBeforeUnmount(() => {
  stopPolling()
  observer?.disconnect()
  if (unsubscribe) unsubscribe()
})
</script>

<template>
  <!-- Das `ref` hier verbindet das DOM-Element mit der `rootEl`-Variable für den IntersectionObserver. -->
  <div ref="rootEl" class="file-view">
    <div class="preview">
      <img v-if="previewType(state?.type) === 'image'" :src="displaySrc" alt="" />
      <video v-else-if="previewType(state?.type) === 'video'" :src="displaySrc" controls />
      <audio v-else-if="previewType(state?.type) === 'audio'" :src="displaySrc" controls />
      <iframe v-else-if="previewType(state?.type) === 'pdf'" :src="displaySrc"></iframe>
      <div v-else class="file-placeholder">Datei</div>
    </div>

    <div class="bar">
      <div class="fill" :style="{ width: (state?.progress || 0) + '%' }"></div>
    </div>

    <div class="info">
      <span>{{ statusLabel }}</span>
      <span>{{ Math.round(state?.progress || 0) }}%</span>
      <span>
        {{ formatMB(state?.uploaded || 0) }}MB /
        {{ formatMB(state?.total || 0) }}MB
      </span>
    </div>

    <a class="btn" :href="downloadHref" download>
      Herunterladen
    </a>
  </div>
</template>

<style scoped>
.file-view {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.preview img,
.preview video,
.preview audio,
.preview iframe {
  width: 100%;
  max-height: 300px;
  border-radius: 8px;
}

.bar {
  height: 8px;
  background: #333;
  border-radius: 4px;
  overflow: hidden;
}

.fill {
  height: 100%;
  background: #4caf50;
  transition: width 0.3s ease;
}

.info {
  font-size: 13px;
  opacity: 0.8;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  padding: 8px 12px;
  background: #1976d2;
  color: white;
  border-radius: 6px;
  text-align: center;
  text-decoration: none;
}
</style>